"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Briefcase,
  FileText,
  Receipt,
  UserPlus,
  Calendar,
  Settings,
  Users,
  BarChart3,
  Search,
  ArrowRight,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useQuickActions } from "./QuickActionsProvider";
import { useTranslations } from "@/hooks/useTranslations";

interface Command {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  category: "actions" | "navigation" | "recent";
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const router = useRouter();
  const quickActions = useQuickActions();
  const t = useTranslations();
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Define all available commands
  const commands: Command[] = useMemo(
    () => [
      // Quick Actions
      {
        id: "new-job",
        title: t("quickActions.commands.newJob"),
        subtitle: t("quickActions.commands.newJobDescription"),
        icon: <Briefcase className="h-4 w-4" />,
        action: () => {
          onClose();
          quickActions.openJobModal();
        },
        keywords: ["create", "add", "job", "task"],
        category: "actions",
      },
      {
        id: "new-quote",
        title: t("quickActions.commands.newQuote"),
        subtitle: t("quickActions.commands.newQuoteDescription"),
        icon: <FileText className="h-4 w-4" />,
        action: () => {
          onClose();
          quickActions.openQuoteModal();
        },
        keywords: ["create", "add", "quote", "estimate"],
        category: "actions",
      },
      {
        id: "new-invoice",
        title: t("quickActions.commands.newInvoice"),
        subtitle: t("quickActions.commands.newInvoiceDescription"),
        icon: <Receipt className="h-4 w-4" />,
        action: () => {
          onClose();
          quickActions.openInvoiceModal();
        },
        keywords: ["create", "add", "invoice", "bill"],
        category: "actions",
      },
      {
        id: "new-client",
        title: t("quickActions.commands.newClient"),
        subtitle: t("quickActions.commands.newClientDescription"),
        icon: <UserPlus className="h-4 w-4" />,
        action: () => {
          onClose();
          quickActions.openClientModal();
        },
        keywords: ["create", "add", "client", "customer"],
        category: "actions",
      },
      // Navigation
      {
        id: "go-dashboard",
        title: t("quickActions.commands.dashboard"),
        subtitle: t("quickActions.commands.dashboardDescription"),
        icon: <BarChart3 className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/dashboard");
        },
        keywords: ["dashboard", "home", "overview"],
        category: "navigation",
      },
      {
        id: "go-jobs",
        title: t("quickActions.commands.jobs"),
        subtitle: t("quickActions.commands.jobsDescription"),
        icon: <Briefcase className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/jobs");
        },
        keywords: ["jobs", "tasks", "work"],
        category: "navigation",
      },
      {
        id: "go-quotes",
        title: t("quickActions.commands.quotes"),
        subtitle: t("quickActions.commands.quotesDescription"),
        icon: <FileText className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/quotes");
        },
        keywords: ["quotes", "estimates"],
        category: "navigation",
      },
      {
        id: "go-invoices",
        title: t("quickActions.commands.invoices"),
        subtitle: t("quickActions.commands.invoicesDescription"),
        icon: <Receipt className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/invoices");
        },
        keywords: ["invoices", "bills", "payments"],
        category: "navigation",
      },
      {
        id: "go-clients",
        title: t("quickActions.commands.clients"),
        subtitle: t("quickActions.commands.clientsDescription"),
        icon: <Users className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/clients");
        },
        keywords: ["clients", "customers"],
        category: "navigation",
      },
      {
        id: "go-schedule",
        title: t("quickActions.commands.schedule"),
        subtitle: t("quickActions.commands.scheduleDescription"),
        icon: <Calendar className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/schedule");
        },
        keywords: ["schedule", "calendar", "appointments"],
        category: "navigation",
      },
      {
        id: "go-settings",
        title: t("quickActions.commands.settings"),
        subtitle: t("quickActions.commands.settingsDescription"),
        icon: <Settings className="h-4 w-4" />,
        action: () => {
          onClose();
          router.push("/settings");
        },
        keywords: ["settings", "preferences", "configuration"],
        category: "navigation",
      },
    ],
    [router, onClose, quickActions, t]
  );

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search) return commands;

    const searchLower = search.toLowerCase();
    return commands.filter((command) => {
      const titleMatch = command.title.toLowerCase().includes(searchLower);
      const subtitleMatch = command.subtitle?.toLowerCase().includes(searchLower);
      const keywordsMatch = command.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower)
      );
      return titleMatch || subtitleMatch || keywordsMatch;
    });
  }, [search, commands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, Command[]> = {
      actions: [],
      navigation: [],
    };

    filteredCommands.forEach((command) => {
      groups[command.category].push(command);
    });

    return groups;
  }, [filteredCommands]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const command = filteredCommands[selectedIndex];
        if (command) {
          command.action();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex]);

  // Reset state when closing
  const handleClose = () => {
    setSearch("");
    setSelectedIndex(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="p-0 overflow-hidden sm:max-w-[600px]">
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("quickActions.searchPlaceholder")}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            ESC
          </kbd>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("quickActions.noResults", { query: search })}
            </div>
          ) : (
            <>
              {groupedCommands.actions.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t("quickActions.categories.actions")}
                  </div>
                  {groupedCommands.actions.map((command, index) => {
                    const globalIndex = filteredCommands.indexOf(command);
                    return (
                      <CommandItem
                        key={command.id}
                        command={command}
                        isSelected={globalIndex === selectedIndex}
                        onClick={command.action}
                      />
                    );
                  })}
                </div>
              )}

              {groupedCommands.navigation.length > 0 && (
                <div>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t("quickActions.categories.navigation")}
                  </div>
                  {groupedCommands.navigation.map((command) => {
                    const globalIndex = filteredCommands.indexOf(command);
                    return (
                      <CommandItem
                        key={command.id}
                        command={command}
                        isSelected={globalIndex === selectedIndex}
                        onClick={command.action}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>{t("quickActions.keyboard.navigateWithArrows")}</span>
            <div className="flex items-center gap-2">
              <kbd className="inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                ↑↓
              </kbd>
              <span>{t("quickActions.keyboard.toNavigate")}</span>
              <kbd className="inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                ↵
              </kbd>
              <span>{t("quickActions.keyboard.toSelect")}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommandItem({
  command,
  isSelected,
  onClick,
}: {
  command: Command;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        isSelected
          ? "bg-accent text-accent-foreground"
          : "hover:bg-accent/50"
      )}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-md border bg-background">
        {command.icon}
      </div>
      <div className="flex-1 text-left">
        <div className="font-medium">{command.title}</div>
        {command.subtitle && (
          <div className="text-xs text-muted-foreground">{command.subtitle}</div>
        )}
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

// Command Palette Provider with Cmd+K trigger
export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  // Register Cmd+K / Ctrl+K shortcut
  useKeyboardShortcuts([
    {
      key: "k",
      metaKey: true,
      description: "Open command palette",
      callback: () => setIsOpen(true),
    },
    {
      key: "k",
      ctrlKey: true,
      description: "Open command palette",
      callback: () => setIsOpen(true),
    },
  ]);

  return (
    <>
      {children}
      <CommandPalette isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
